import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Route, Droplets, Settings, LogOut, UserCircle2, Download, ShieldAlert, RotateCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { IS_TEST_MODE } from "../lib/testMode";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const NAV_ITEMS_FULL = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["AMMINISTRATORE", "UFFICIO"] },
  { to: "/produttori", label: "Produttori", icon: Users, testid: "nav-produttori", roles: ["AMMINISTRATORE", "UFFICIO"] },
  { to: "/giro", label: "Giro", icon: Route, testid: "nav-giro", roles: ["AMMINISTRATORE", "UFFICIO", "TRASPORTATORE"] },
  { to: "/ritiri", label: "Ritiri", icon: Droplets, testid: "nav-ritiri", roles: ["AMMINISTRATORE", "UFFICIO"] },
  { to: "/recuperi", label: "Recuperi", icon: RotateCw, testid: "nav-recuperi", roles: ["AMMINISTRATORE", "UFFICIO"] },
];

export default function Layout({ children }) {
  const location = useLocation();
  const nav = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const onSettings = location.pathname.startsWith("/settings");

  const navItems = NAV_ITEMS_FULL.filter((it) => !it.roles || it.roles.includes(user?.ruolo));
  const cols = navItems.length;

  const onLogout = async () => {
    await logout();
    // Hard reload dopo logout: pulisce qualsiasi stato React residuo,
    // bundle JS in cache, autofill stale e tab inconsistenti. Evita problemi
    // di re-login dopo logout segnalati dai test utente (cache bundle vecchio).
    window.location.replace("/login");
  };

  return (
    <div className="min-h-screen bg-[#F4F7F4]">
      {/* Top bar */}
      <header className={`sticky ${IS_TEST_MODE ? "top-9" : "top-0"} z-40 bg-[#1B5E20] text-white shadow-sm`}>
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-[#F57C00] flex items-center justify-center font-bold" aria-hidden>
              <Droplets size={18} />
            </div>
            <div>
              <div className="font-semibold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                Olio Esausto
                {IS_TEST_MODE && (
                  <span data-testid="test-mode-header-badge" className="ml-2 px-1.5 py-0.5 rounded bg-red-600 text-[10px] font-bold align-middle tracking-wider">TEST</span>
                )}
              </div>
              <div className="text-[11px] text-white/70 leading-tight">{IS_TEST_MODE ? "Ambiente test — dati non reali" : "Gestione ritiri"}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {(isAdmin || user?.ruolo === "UFFICIO") && (
              <NavLink
                to="/settings"
                data-testid="nav-settings"
                className={`h-10 w-10 rounded-lg flex items-center justify-center transition-colors ${onSettings ? "bg-white/20" : "hover:bg-white/10"}`}
                aria-label="Impostazioni"
              >
                <Settings size={20} />
              </NavLink>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="h-10 px-2 rounded-lg flex items-center gap-1.5 hover:bg-white/10 transition-colors min-h-[40px]"
                  data-testid="user-menu-trigger"
                  aria-label="Menu utente"
                >
                  <UserCircle2 size={20} />
                  <span className="text-xs font-semibold hidden sm:inline">{user?.nome || user?.email?.split("@")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs font-semibold">{user?.nome} {user?.cognome}</div>
                  <div className="text-[11px] text-slate-500">{user?.email}</div>
                  <div className="text-[10px] uppercase mt-1 tracking-wider text-[#1B5E20] font-bold">{user?.ruolo}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <DropdownMenuItem onClick={() => nav("/utenti")} data-testid="menu-users">
                    <Users size={14} className="mr-2" /> Gestione utenti
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem onClick={() => nav("/login-attempts")} data-testid="menu-login-attempts">
                    <ShieldAlert size={14} className="mr-2" /> Tentativi falliti
                  </DropdownMenuItem>
                )}
                {(isAdmin || user?.ruolo === "UFFICIO") && (
                  <DropdownMenuItem onClick={() => nav("/esportazioni")} data-testid="menu-esportazioni">
                    <Download size={14} className="mr-2" /> Esportazioni
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => nav("/cambio-password")} data-testid="menu-change-pwd">
                  Cambia password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="menu-logout" className="text-[#C62828]">
                  <LogOut size={14} className="mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 pb-28">{children}</main>

      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 pb-safe"
        style={{ boxShadow: "0 -4px 16px rgba(0,0,0,0.05)" }}
      >
        <div className="max-w-6xl mx-auto grid h-16" style={{ gridTemplateColumns: `repeat(${cols || 1}, minmax(0, 1fr))` }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 min-h-[44px] transition-colors ${
                    isActive ? "text-[#1B5E20]" : "text-slate-500"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={22} strokeWidth={isActive ? 2.4 : 2} />
                    <span className={`text-[11px] ${isActive ? "font-semibold" : "font-medium"}`}>{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
